/**
 * SDK model update diffing
 *
 * Builds a minimal update payload from a desired model snapshot: it keeps only the
 * top-level fields that changed and derives whether the update must be sent with
 * `replaceObjectFields` semantics (see `docs/replaceObjectFields.md`).
 */

import { deepEqual } from "./object-diff";

export interface ModelUpdateDiffResult<T extends object> {
  /** Only the top-level fields whose value differs between `original` and `next`. */
  updates: Partial<T>;
  /**
   * True when at least one changed field holds an array or plain object. For those
   * value types the default merge/append semantics cannot reproduce `next` (stale array
   * items are left behind, object keys cannot be removed, `{}`/`[]` cannot clear a
   * value), so the update must be sent with `_options.replaceObjectFields: true`.
   * Always false when only primitive / `Date` / `null` fields changed — the flag has no
   * effect on those.
   */
  replaceObjectFields: boolean;
  /** Whether any field changed at all. */
  hasChanges: boolean;
}

export interface ModelUpdateDiffOptions<T extends object> {
  /** Top-level keys to exclude from the diff entirely. */
  ignoreFields?: (keyof T)[];
  /** Per-field equality override; return true to treat the two values as equal. */
  isEqual?: (originalValue: unknown, nextValue: unknown, key: keyof T) => boolean;
}

const isReplaceTriggeringValue = (value: unknown): boolean =>
  Array.isArray(value) || (typeof value === "object" && value !== null && !(value instanceof Date));

/**
 * Diffs a desired model snapshot (`next`) against its original (`original`) and returns
 * the minimal update payload plus the correct `replaceObjectFields` flag.
 *
 * The diff is driven entirely off `next`'s own keys, so `next` may be a partial snapshot:
 * - A key **absent from `next`** is treated as "leave it alone" — it is never compared and
 *   never appears in `updates` (this matches SDK update semantics: omitting a field does
 *   not remove it).
 * - A key present on `next` but absent from `original` diffs as a real change.
 * - A key whose `next` value is `undefined` is skipped — `undefined` is not a sendable
 *   update value (`JSON.stringify` drops it) and is indistinguishable from "not provided".
 *   Clear a field with `null`, not `undefined`.
 *
 * `replaceObjectFields` is a single payload-wide flag, so the rule is "replace if any
 * changed field is an array or plain object". This assumes `next` holds the complete
 * desired value of each field (a snapshot), which is the typical edit-form case — pass a
 * delta and the appended/merged result will not match `next`.
 *
 * @example
 * ```typescript
 * const { updates, replaceObjectFields, hasChanges } = diffModelUpdate(user, {
 *   fname: "Jane",            // primitive change  -> replaceObjectFields stays false
 *   suffixes: ["MD"],          // array change      -> replaceObjectFields becomes true
 * });
 * if (hasChanges) {
 *   await sdk.api.users.updateOne(id, updates, { replaceObjectFields });
 * }
 * ```
 */
export const diffModelUpdate = <T extends object>(
  original: Partial<Record<keyof T, unknown>> | undefined,
  next: Partial<T>,
  options: ModelUpdateDiffOptions<T> = {}
): ModelUpdateDiffResult<T> => {
  const { ignoreFields = [], isEqual } = options;
  const ignored = new Set<keyof T>(ignoreFields);

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Object.keys is typed as string[]; its keys are necessarily keyof T
  const keys = Object.keys(next) as (keyof T)[];

  const result = keys.reduce<ModelUpdateDiffResult<T>>(
    (acc, key) => {
      if (ignored.has(key)) return acc;

      const nextValue = next[key];
      if (nextValue === undefined) return acc;

      const originalValue = original?.[key];
      const equal = isEqual
        ? isEqual(originalValue, nextValue, key)
        : // A reordered array field is a real change for an update payload, so keep order
          // significant rather than inheriting deepEqual's order-insensitive default.
          deepEqual(originalValue, nextValue, { includeRemovals: true, orderSensitive: true });
      if (equal) return acc;

      acc.updates[key] = nextValue;
      acc.hasChanges = true;
      if (isReplaceTriggeringValue(nextValue)) acc.replaceObjectFields = true;
      return acc;
    },
    { updates: {}, replaceObjectFields: false, hasChanges: false }
  );

  return result;
};
